import { BoutonVersionsRecu } from '../recus/RecuVersions';
import { ouvrirPdf } from '../recus/ouvrirPdf';
import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Download, CheckCircle, Clock, Eye, Banknote, Hash, Share2, Calendar, CreditCard, Repeat, Building2, Activity, Send, MapPin, Mail, Printer } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

import { TYPE_CONFIG } from './constants';

export default function DocumentCard({
  doc, calculateMontantTTC, calculateMontantHT, selectedIds,
  toggleSelection, openValidationModal, openPartialPaymentModal,
  openEcheanceModal: openEcheanceModal, openEcheancierModal: openEcheancierModal, openStripeSepaModal,
  openShareModal, downloadRecu, viewRecu, printEcheancier: printEcheancier,
  handleDeleteEcheancier: handleDeleteEcheancier, handleResetEcheancier: handleResetEcheancier,
  initiateStripeCBPayment, generatingRecu: generatingRecu, scheduleSepaDebit: scheduleSepaDebit,
  activeTab, setSelectedDocument, setAssignForm, setShowAssignModal
}) {
    const { getAuthHeader } = useAuth();
    const isAvenant = doc.type_document === 'avenant' || doc.numero_police?.startsWith('AV-');
    const montantTTC = calculateMontantTTC(doc);
    const _montantHT = calculateMontantHT(doc);
    const typeConfig = TYPE_CONFIG[isAvenant ? 'avenant' : 'contrat'];
    const TypeIcon = typeConfig.icon;
    const isSelected = selectedIds.has(doc.id);
    
    // Calcul des paiements partiels
    const paiementsPartiels = doc.paiements_partiels || [];
    const totalPaye = doc.total_paye || paiementsPartiels.reduce((sum, p) => sum + (p.montant || 0), 0);
    const resteAPayer = doc.reste_a_payer ?? (montantTTC - totalPaye);
    const pourcentagePaye = montantTTC > 0 ? (totalPaye / montantTTC * 100) : 0;
    const hasPaiementsPartiels = paiementsPartiels.length > 0 && !doc.paiement_valide;

    return (
      <Card 
        key={doc.id} 
        className={`group hover:shadow-2xl transition-all duration-300 overflow-hidden ${
          isSelected ? 'ring-2 ring-blue-500 shadow-blue-100' : ''
        } ${
          isAvenant 
            ? 'border-l-4 border-l-purple-500 bg-white' 
            : 'border-l-4 border-l-blue-500 bg-white'
        }`}
      >
        <CardContent className="p-0">
          {/* Header avec badge type et statut */}
          <div className={`px-5 py-3 ${isAvenant ? 'bg-purple-50' : 'bg-blue-50'} border-b`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Checkbox de sélection */}
                {activeTab === 'attente' && !doc.paiement_valide && (
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleSelection(doc.id)}
                    className="h-5 w-5 border-2"
                  />
                )}
                <div className={`p-2 rounded-lg ${isAvenant ? 'bg-purple-500' : 'bg-blue-500'}`}>
                  <TypeIcon className="h-4 w-4 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-bold ${isAvenant ? 'text-purple-700' : 'text-blue-700'}`}>
                      {typeConfig.label.toUpperCase()}
                    </span>
                    {hasPaiementsPartiels && (
                      <Badge className="bg-orange-500 text-white text-xs px-2 py-0.5 font-semibold">
                        <Activity className="h-3 w-3 mr-1" />
                        {paiementsPartiels.length} paiement(s)
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 font-mono">
                    {(doc.numero_police || doc.id || '').substring(0, 25)}
                  </p>
                </div>
              </div>
              
              {/* Statut */}
              <div className="flex items-center gap-2 flex-wrap">
                {doc.paiement_valide ? (
                  <Badge className="bg-emerald-500 text-white font-semibold px-3 py-1">
                    <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                    Payé
                  </Badge>
                ) : hasPaiementsPartiels ? (
                  <Badge className="bg-orange-500 text-white font-semibold px-3 py-1">
                    <Clock className="h-3.5 w-3.5 mr-1.5" />
                    Partiel
                  </Badge>
                ) : (
                  <Badge className="bg-amber-500 text-white font-semibold px-3 py-1">
                    <Clock className="h-3.5 w-3.5 mr-1.5" />
                    En attente
                  </Badge>
                )}
                {/* Badge SEPA RCD Solidaire (V35.35) - synchronisé par /api/gocardless/admin */}
                {doc.sepa_payment_status && doc.sepa_payment_status !== 'paye' && (
                  <Badge
                    className={
                      doc.sepa_payment_status === 'partiel'
                        ? 'bg-[#DEA900] text-[#0a3d62] font-semibold px-3 py-1'
                        : 'bg-[#0a3d62] text-[#f5cc3e] font-semibold px-3 py-1'
                    }
                    data-testid={`badge-sepa-${doc.sepa_payment_status}-${doc.id}`}
                    title={`SEPA ${doc.sepa_paid_count || 0}/${doc.sepa_total_count || 3} · Restant ${(((doc.sepa_total_cents || 0) - (doc.sepa_paid_cents || 0)) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`}
                  >
                    SEPA {doc.sepa_payment_status === 'partiel' ? 'partiel' : 'en attente'}
                    {doc.sepa_total_count ? ` · ${doc.sepa_paid_count || 0}/${doc.sepa_total_count}` : ''}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Corps de la carte */}
          <div className="p-5 space-y-4">
            {/* Entreprise */}
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-gray-100">
                <Building2 className="h-5 w-5 text-gray-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-900 text-lg truncate" data-testid={`doc-card-raison-${doc.id}`}>
                  {doc.raison_sociale || 'Non renseigné'}
                </h3>
                <div className="flex items-center gap-4 text-sm text-gray-500 mt-1 flex-wrap">
                  {doc.siret && (
                    <span className="flex items-center gap-1" data-testid={`doc-card-siret-${doc.id}`}>
                      <Hash className="h-3.5 w-3.5" />
                      {doc.siret}
                    </span>
                  )}
                  {/* V36.89 — Territoires (string CSV ou liste) */}
                  {(() => {
                    const raw = doc.territoires || doc.departements || doc.territoire || doc.departement;
                    let list = [];
                    if (Array.isArray(raw)) list = raw.filter(Boolean).map(String);
                    else if (typeof raw === 'string') list = raw.split(',').map(s => s.trim()).filter(Boolean);
                    if (!list.length) return null;
                    return (
                      <span className="flex items-center gap-1" data-testid={`doc-card-territoires-${doc.id}`} title="Territoires">
                        <MapPin className="h-3.5 w-3.5" />
                        {list.join(' · ')}
                      </span>
                    );
                  })()}
                  {/* V36.89 — Email souscripteur */}
                  {(doc.user_email || doc.email) && (
                    <span className="flex items-center gap-1" data-testid={`doc-card-email-${doc.id}`}>
                      <Mail className="h-3.5 w-3.5" />
                      {doc.user_email || doc.email}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {new Date(doc.date_creation || doc.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              </div>
            </div>

            {/* Barre de progression des paiements (si paiements partiels) */}
            {hasPaiementsPartiels && (
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-200">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="font-semibold text-orange-700">Progression du paiement</span>
                  <span className="font-bold text-orange-600">{pourcentagePaye.toFixed(0)}%</span>
                </div>
                <div className="h-3 bg-orange-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-orange-400 to-amber-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, pourcentagePaye)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-600 mt-2">
                  <span>Payé: <strong className="text-green-600">{totalPaye.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</strong></span>
                  <span>Reste: <strong className="text-red-600">{resteAPayer.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</strong></span>
                </div>
              </div>
            )}

            {/* Montants */}
            <div className={`grid grid-cols-2 gap-3`}>
              <div className={`p-4 rounded-xl ${isAvenant ? 'bg-purple-50 border border-purple-100' : 'bg-blue-50 border border-blue-100'}`}>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Montant Global</p>
                <p className="text-xl font-bold text-gray-800">
                  {montantTTC.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                </p>
                {totalPaye > 0 && (
                  <p className="text-xs text-emerald-600 font-semibold mt-1">Paye: {totalPaye.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                )}
              </div>
              <div className={`p-4 rounded-xl ${doc.paiement_valide ? 'bg-emerald-50 border border-emerald-200' : resteAPayer > 0.01 ? 'bg-red-50 border-2 border-red-300' : 'bg-emerald-50 border border-emerald-200'}`}>
                {doc.paiement_valide || resteAPayer <= 0.01 ? (
                  <>
                    <p className="text-xs text-emerald-600 uppercase tracking-wide mb-1">Acquitte</p>
                    <p className="text-xl font-black text-emerald-600">
                      {montantTTC.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-red-600 uppercase tracking-wide mb-1 font-bold">Reste a payer</p>
                    <p className="text-xl font-black text-red-600">
                      {resteAPayer.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="pt-2 border-t">
              {activeTab === 'attente' ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => openValidationModal(doc)}
                      className={`flex-1 ${
                        isAvenant 
                          ? 'bg-purple-600 hover:bg-purple-700' 
                          : 'bg-blue-600 hover:bg-blue-700'
                      } text-white shadow-lg`}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Valider paiement
                    </Button>
                    <Button 
                      onClick={() => openPartialPaymentModal(doc)}
                      variant="outline"
                      className="text-orange-600 border-orange-300 hover:bg-orange-50 shadow"
                      title="Paiement partiel"
                    >
                      <Banknote className="h-4 w-4" />
                    </Button>
                    <Button 
                      onClick={() => {
                        setSelectedDocument(doc);
                        setAssignForm({ assign_type: '', assign_id: '' });
                        setShowAssignModal(true);
                      }}
                      variant="outline"
                      className="text-gray-600 border-gray-300 hover:bg-gray-50 shadow"
                      title="Assigner"
                    >
                      <Share2 className="h-4 w-4" />
                    </Button>
                  </div>
                  {/* Boutons Visualiser & Télécharger pour reçus en attente */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => viewRecu(doc)}
                      variant="outline"
                      size="sm"
                      className="flex-1 text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                      data-testid={`view-recu-attente-${doc.id}`}
                    >
                      <Eye className="h-3.5 w-3.5 mr-1.5" />
                      Visualiser
                    </Button>
                    <Button
                      onClick={() => downloadRecu(doc)}
                      variant="outline"
                      size="sm"
                      className="flex-1 text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                      data-testid={`download-recu-attente-${doc.id}`}
                    >
                      <Download className="h-3.5 w-3.5 mr-1.5" />
                      Télécharger
                    </Button>
                  </div>
                  {!isAvenant && (
                    <div className="grid grid-cols-2 gap-2">
                      <BoutonVersionsRecu produit="rcds" ident={doc.id} titre={doc.numero_police || doc.raison_sociale || doc.id} className="h-8 w-full" testId={`versions-recu-attente-${doc.id}`} />
                      <Button size="sm" variant="outline" className="h-8 w-full" data-testid={`echeancier-attente-${doc.id}`}
                        onClick={() => ouvrirPdf(`${process.env.REACT_APP_BACKEND_URL}/api/souscriptions/${doc.id}/echeancier-pdf`, getAuthHeader(), `Echeancier_${doc.id}.pdf`)}>
                        <Printer className="h-3.5 w-3.5 mr-1.5" /> Échéancier
                      </Button>
                    </div>
                  )}
                  {/* Boutons Stripe SEPA & CB */}
                  <div className="flex gap-2 pt-2 border-t border-dashed border-gray-200">
                    <Button
                      onClick={() => openStripeSepaModal(doc, 'core')}
                      variant="outline"
                      size="sm"
                      className="flex-1 text-blue-600 border-blue-300 hover:bg-blue-50"
                      title="Configurer SEPA Core (particuliers)"
                    >
                      <Repeat className="h-3 w-3 mr-1" />
                      SEPA Core
                    </Button>
                    <Button
                      onClick={() => openStripeSepaModal(doc, 'b2b')}
                      variant="outline"
                      size="sm"
                      className="flex-1 text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                      title="Configurer SEPA B2B (entreprises)"
                    >
                      <Building2 className="h-3 w-3 mr-1" />
                      SEPA B2B
                    </Button>
                    <Button
                      onClick={() => initiateStripeCBPayment(doc)}
                      variant="outline"
                      size="sm"
                      className="flex-1 text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                      title="Paiement CB Stripe"
                    >
                      <CreditCard className="h-3 w-3 mr-1" />
                      CB Stripe
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    onClick={() => viewRecu(doc)}
                    className={`${
                      isAvenant 
                        ? 'bg-purple-600 hover:bg-purple-700' 
                        : 'bg-blue-600 hover:bg-blue-700'
                    } text-white shadow-lg`}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Voir reçu
                  </Button>
                  <Button 
                    onClick={() => downloadRecu(doc)}
                    variant="outline"
                    className="shadow"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Télécharger
                  </Button>
                  {!isAvenant && <BoutonVersionsRecu produit="rcds" ident={doc.id} titre={doc.numero_police || doc.raison_sociale || doc.id} className="h-9 whitespace-nowrap" />}
                  {!isAvenant && (
                    <Button size="sm" variant="outline" className="h-9 whitespace-nowrap" data-testid={`echeancier-${doc.id}`}
                      onClick={() => ouvrirPdf(`${process.env.REACT_APP_BACKEND_URL}/api/souscriptions/${doc.id}/echeancier-pdf`, getAuthHeader(), `Echeancier_${doc.id}.pdf`)}>
                      <Printer className="h-4 w-4 mr-2" /> Échéancier
                    </Button>
                  )}
                  <Button 
                    onClick={() => openShareModal(doc)}
                    variant="outline"
                    className="text-green-600 border-green-300 hover:bg-green-50 shadow"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Envoyer
                  </Button>
                  <Button 
                    onClick={() => {
                      setSelectedDocument(doc);
                      setAssignForm({ assign_type: '', assign_id: '' });
                      setShowAssignModal(true);
                    }}
                    variant="outline"
                    className="text-purple-600 border-purple-300 hover:bg-purple-50 shadow"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Assigner
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
}
